import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalComponentBaseProps, useModal } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody } from '@ingka/modal';
import { useContentTracking } from '@/lib/hooks/tracking';
import ListView, { ListViewItem } from '@ingka/list-view';
import FaqQuestions from '../questions';

type Section = 'userFaq' | 'adminFaq';

const FaqMenuSider: FC<ModalComponentBaseProps> = ({
  handleDismiss,
  ...rest
}) => {
  // Hooks
  const trackContent = useContentTracking();
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  // Event handlers
  const goToSection = (section: Section) => async () => {
    await closeModal(0);
    await openModal({
      component: FaqQuestions,
      props: {
        faqType: section === 'userFaq' ? 'users' : 'admins',
      },
    });
  };

  useEffect(() => {
    trackContent('faq', { type: 'sider' });
  }, []);

  const goBack = async () => {
    await closeModal(0);
    const { default: HelpCenterSider } = await import('../..');

    await openModal({
      component: HelpCenterSider,
      props: {},
    });
  };

  return (
    <Sheets
      {...rest}
      data-testid="faqMenuModal"
      aria-label={t('page.faq.description')}
      footer={null}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t('page.faq.title')}
          backBtnClick={goBack}
        />
      }
    >
      <ModalBody>
        <ListView id="help-center-list">
          <ListViewItem
            data-testid="userFaqBtn"
            size="medium"
            inset
            emphasised
            title={t('page.faq.users.title')}
            description={t('page.faq.users.description')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('userFaq')}
          />
          <ListViewItem
            data-testid="adminFaqBtn"
            size="medium"
            inset
            emphasised
            title={t('page.faq.admins.title')}
            description={t('page.faq.admins.description')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('adminFaq')}
          />
        </ListView>
      </ModalBody>
    </Sheets>
  );
};

export default FaqMenuSider;
